/**
 * ResearchPaperPage — Research Paper generator.
 *
 * Supports three paper structures:
 *   IMRaD            — Introduction, Methods, Results, Discussion
 *   Standard Academic — Introduction, Literature Review, Methods, Discussion, Conclusion
 *   Theory Paper      — Introduction, Theoretical Framework, Analysis, Implications, Conclusion
 */

import { useState, useEffect, useRef } from 'react'
import { marked } from 'marked'
import { supabase } from '../../lib/supabase'
import { streamAnthropic } from '../../lib/anthropic'
import { useAIStore } from '../../store/aiStore'
import { buildResearchPaperPrompt } from '../../prompts/researchPaper'
import DocumentEditor from '../../components/documents/DocumentEditor'
import { Sparkles, ChevronRight, ArrowLeft } from 'lucide-react'
import toast from 'react-hot-toast'

// ─── Primitives ───────────────────────────────────────────────────────────────

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="block font-sans text-sm font-medium text-acdyon-textMuted mb-1.5">
        {label}
        {required && <span style={{ color: '#2563eb' }}> *</span>}
      </label>
      {hint && <p className="font-sans text-xs text-acdyon-textFaint mb-2">{hint}</p>}
      {children}
    </div>
  )
}

const inputCls   = "w-full px-3.5 py-2.5 rounded-lg font-sans text-sm text-acdyon-text placeholder:text-acdyon-textFaint outline-none transition-colors"
const inputStyle = { background: '#FFFFFF', border: '1px solid #C8D4E4' }
const inputFocus = {
  onFocus: e => (e.currentTarget.style.borderColor = '#2563eb'),
  onBlur:  e => (e.currentTarget.style.borderColor = '#C8D4E4'),
}

// ─── Paper structure cards ────────────────────────────────────────────────────

const STRUCTURES = [
  {
    id:    'IMRaD',
    label: 'IMRaD',
    desc:  'Introduction · Methods · Results · Discussion',
    note:  'Standard for empirical / experimental papers',
  },
  {
    id:    'Standard Academic',
    label: 'Standard Academic',
    desc:  'Introduction · Lit Review · Methods · Discussion · Conclusion',
    note:  'Social sciences, humanities, education',
  },
  {
    id:    'Theory Paper',
    label: 'Theory Paper',
    desc:  'Introduction · Theoretical Framework · Analysis · Implications · Conclusion',
    note:  'Conceptual / philosophical contributions',
  },
]

function StructureCards({ value, onChange }) {
  return (
    <div className="space-y-2">
      {STRUCTURES.map(s => (
        <button
          key={s.id}
          type="button"
          onClick={() => onChange(s.id)}
          className="w-full text-left px-4 py-3.5 rounded-xl transition-all"
          style={{
            background: value === s.id ? 'rgba(37,99,235,0.08)' : '#F4F7FC',
            border:     `1px solid ${value === s.id ? 'rgba(37,99,235,0.4)' : '#DDE4EF'}`,
          }}
        >
          <div className="flex items-center justify-between">
            <p className="font-sans text-sm font-semibold" style={{ color: value === s.id ? '#2563eb' : '#0C1629' }}>
              {s.label}
            </p>
            <div
              className="w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all"
              style={{ borderColor: value === s.id ? '#2563eb' : '#DDE4EF' }}
            >
              {value === s.id && <div className="w-2 h-2 rounded-full" style={{ background: '#2563eb' }} />}
            </div>
          </div>
          <p className="font-mono text-xs text-acdyon-textFaint mt-0.5">{s.desc}</p>
          <p className="font-sans text-xs text-acdyon-textFaint mt-1">{s.note}</p>
        </button>
      ))}
    </div>
  )
}

// ─── Existing doc card ────────────────────────────────────────────────────────

function ExistingCard({ doc, onEdit, onNew }) {
  return (
    <div className="rounded-2xl p-6 space-y-4" style={{ background: '#FFFFFF', border: '1px solid #DDE4EF', maxWidth: 520 }}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.2)' }}>
          <Sparkles style={{ width: 18, height: 18, color: '#2563eb' }} />
        </div>
        <div>
          <p className="font-sans font-semibold text-acdyon-text text-sm">{doc.title}</p>
          <p className="font-mono text-xs text-acdyon-textFaint mt-0.5">
            {(doc.word_count || 0).toLocaleString()} words · {new Date(doc.updated_at).toLocaleDateString()}
          </p>
        </div>
      </div>
      <div className="flex gap-3">
        <button onClick={onEdit} className="flex-1 py-2.5 rounded-xl font-sans text-sm font-medium"
          style={{ background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.3)', color: '#2563eb' }}>
          Continue Editing
        </button>
        <button onClick={onNew} className="flex-1 py-2.5 rounded-xl font-sans text-sm font-medium"
          style={{ background: '#FFFFFF', border: '1px solid #C8D4E4', color: '#506280' }}>
          Generate New
        </button>
      </div>
    </div>
  )
}

// ─── Setup Form ───────────────────────────────────────────────────────────────

function SetupForm({ onGenerate }) {
  const [form, setForm] = useState({
    title:          '',
    coreArgument:   '',
    keyLiterature:  '',
    targetJournal:  '',
    paperStructure: 'Standard Academic',
    targetWords:    5000,
  })

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }))
  const canSubmit = form.title && form.coreArgument

  return (
    <form onSubmit={e => { e.preventDefault(); if (canSubmit) onGenerate(form) }} className="space-y-6">

      {/* Paper title */}
      <Field label="Paper Title" required>
        <input
          className={inputCls} style={inputStyle} {...inputFocus}
          value={form.title}
          onChange={set('title')}
          placeholder="e.g. Examining the Role of Metacognition in Autonomous Learning"
        />
      </Field>

      {/* Core argument / thesis */}
      <Field
        label="Core Argument / Thesis Statement"
        hint="The central claim this paper will make and defend."
        required
      >
        <textarea
          className={inputCls} style={inputStyle} {...inputFocus}
          rows={4}
          value={form.coreArgument}
          onChange={set('coreArgument')}
          placeholder="e.g. This paper argues that metacognitive scaffolding significantly improves self-regulated learning outcomes in online environments, challenging behaviorist models of instructional design…"
        />
      </Field>

      {/* Key literature */}
      <Field
        label="Key Literature"
        hint="Cite 3–6 foundational works (APA or brief descriptions). The AI will weave these in and cite additional sources."
      >
        <textarea
          className={inputCls} style={inputStyle} {...inputFocus}
          rows={4}
          value={form.keyLiterature}
          onChange={set('keyLiterature')}
          placeholder="e.g. Zimmerman (2000) - self-regulated learning framework; Flavell (1979) - metacognition; Bandura (1997) - self-efficacy theory…"
        />
      </Field>

      {/* Target journal */}
      <Field label="Target Journal (optional)" hint="Knowing the journal helps the AI match the right tone and citation density.">
        <input
          className={inputCls} style={inputStyle} {...inputFocus}
          value={form.targetJournal}
          onChange={set('targetJournal')}
          placeholder="e.g. Computers & Education, British Journal of Educational Technology"
        />
      </Field>

      {/* Structure */}
      <Field label="Paper Structure">
        <StructureCards value={form.paperStructure} onChange={v => setForm(prev => ({ ...prev, paperStructure: v }))} />
      </Field>

      {/* Target words */}
      <Field label="Target Length (words)">
        <div className="flex items-center gap-4">
          <input
            type="range" min={3000} max={10000} step={500}
            value={form.targetWords}
            onChange={e => setForm(prev => ({ ...prev, targetWords: Number(e.target.value) }))}
            className="flex-1 accent-brand-600"
          />
          <span className="font-mono text-sm font-semibold text-brand-600 w-16 text-right">
            {form.targetWords.toLocaleString()}
          </span>
        </div>
      </Field>

      <button
        type="submit"
        disabled={!canSubmit}
        className="flex items-center gap-2 px-6 py-3 rounded-xl font-sans text-sm font-semibold transition-all disabled:opacity-50"
        style={{ background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.4)', color: '#2563eb' }}
      >
        <Sparkles style={{ width: 15, height: 15 }} />
        Generate Paper
        <ChevronRight style={{ width: 15, height: 15 }} />
      </button>
    </form>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ResearchPaperPage() {
  const [phase,       setPhase]       = useState('loading')
  const [existingDoc, setExistingDoc] = useState(null)
  const [documentId,  setDocumentId]  = useState(null)
  const [docTitle,    setDocTitle]    = useState('')
  const [docContent,  setDocContent]  = useState('')
  const [streaming,   setStreaming]   = useState(false)
  const [formData,    setFormData]    = useState(null)

  const editorRef = useRef(null)
  const abortRef  = useRef(false)
  const aiStore   = useAIStore()

  useEffect(() => {
    const check = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) { setPhase('form'); return }
        const { data } = await supabase
          .from('documents')
          .select('id, type, title, word_count, updated_at, content')
          .eq('user_id', user.id)
          .eq('type', 'RESEARCH_PAPER')
          .order('updated_at', { ascending: false })
          .limit(1)
        const paper = data?.[0]
        if (paper) { setExistingDoc(paper); setPhase('check') }
        else         setPhase('form')
      } catch { setPhase('form') }
    }
    check()
  }, [])

  const openExisting = () => {
    setDocumentId(existingDoc.id)
    setDocTitle(existingDoc.title)
    setDocContent(existingDoc.content || '')
    setPhase('editor')
  }

  const handleGenerate = async (params) => {
    try {
      aiStore.assertEnabled()
    } catch (err) {
      toast.error(err.message)
      return
    }

    setFormData(params)
    setPhase('editor')
    setStreaming(true)
    abortRef.current = false
    await new Promise(r => setTimeout(r, 120))
    editorRef.current?.clearContent()

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const prompt = buildResearchPaperPrompt(params)
      let fullMarkdown = ''

      await streamAnthropic({
        feature:   'research_paper',
        maxTokens: 10000,
        messages:  [{ role: 'user', content: prompt }],
        onChunk: (text) => {
          if (abortRef.current) return
          fullMarkdown += text
        },
      })

      if (abortRef.current) return

      aiStore.increment()

      const title = params.title || 'Research Paper'
      const wordCount = fullMarkdown.trim().split(/\s+/).length

      const html = await marked.parse(fullMarkdown, { async: false })
      editorRef.current?.setHTML(html)

      const { data: saved } = await supabase.from('documents').insert({
        user_id:    user.id,
        type:       'RESEARCH_PAPER',
        title,
        content:    html,
        word_count: wordCount,
        status:     'DRAFT',
      }).select('id').single()

      if (saved?.id) {
        setDocumentId(saved.id)
        setDocTitle(title)
      }
    } catch (err) {
      console.error('Research paper generation error:', err)
      toast.error(err.message || 'Generation failed')
    } finally {
      setStreaming(false)
    }
  }

  const stopStreaming = () => { abortRef.current = true; setStreaming(false) }

  if (phase === 'loading') {
    return (
      <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 4rem)' }}>
        <div className="w-6 h-6 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
      </div>
    )
  }

  if (phase === 'editor') {
    return (
      <DocumentEditor
        ref={editorRef}
        documentId={documentId}
        initialContent={docContent}
        placeholder="Your research paper begins here…"
        targetWords={formData?.targetWords || 5000}
        docTitle={docTitle}
        streaming={streaming}
        streamingLabel={`Writing "${formData?.title || 'Research Paper'}"…`}
        onStopStreaming={streaming ? stopStreaming : null}
        onSaved={(id) => setDocumentId(id)}
      />
    )
  }

  return (
    <div className="min-h-screen p-8" style={{ background: '#F4F7FC' }}>
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.2)' }}>
              <Sparkles style={{ width: 18, height: 18, color: '#2563eb' }} />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-acdyon-text">Research Paper</h1>
              <p className="font-sans text-sm text-acdyon-textFaint">IMRaD, Standard, or Theory — professor-quality prose</p>
            </div>
          </div>
        </div>

        {/* Existing doc check */}
        {phase === 'check' && existingDoc && (
          <div className="mb-8">
            <p className="font-sans text-sm text-acdyon-textMuted mb-4">
              You have an existing research paper. Continue editing or generate a new one?
            </p>
            <ExistingCard doc={existingDoc} onEdit={openExisting} onNew={() => setPhase('form')} />
          </div>
        )}

        {/* Form */}
        {phase === 'form' && (
          <div className="rounded-2xl p-6" style={{ background: '#FFFFFF', border: '1px solid #DDE4EF' }}>
            {existingDoc && (
              <button
                onClick={() => setPhase('check')}
                className="flex items-center gap-1.5 mb-5 font-sans text-sm text-acdyon-textMuted hover:text-acdyon-text transition-colors"
              >
                <ArrowLeft style={{ width: 14, height: 14 }} />
                Back
              </button>
            )}
            <h2 className="font-sans text-sm font-semibold text-acdyon-text mb-1">Paper Details</h2>
            <p className="font-sans text-xs text-acdyon-textFaint mb-6">
              Provide your argument and key sources — AI will structure and write the full paper.
            </p>
            <SetupForm onGenerate={handleGenerate} />
          </div>
        )}
      </div>
    </div>
  )
}
